import React from "react";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import { colors } from "style";
import { Row } from "components/Containers";
import { DestructableButton, PrimaryButton, SecondaryButton, BUTTON_SIZE } from "components/Buttons";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";
import { Spinner } from "components/Loader";
import { InlineProgressBar } from "components/Progress";
import Tags from "components/Tags";
import { TAG_COLORS, TAG_SIZE } from "components/Tags/Tag";
import Status from "components/Status";
import { RoleLink } from "components/Missions/Roles";
import { AIRoleBuilderTaskStage, taskDisplayName } from "components/Missions/Roles/AIRoleBuilder";
import {
  AI_ROLE_BUILDER_TASK_DISPLAY_STATE as TASK_DISPLAY_STATE,
  getRoleBuilderTaskDisplayState,
  ROLE_BUILDER_STEP,
  ROLE_BUILDER_STEPS,
  getRoleBuilderStepIndex,
} from "constants/index";

/**
 * Columns for the Talent Simple Draft Roles list, where each row holds either an AI role builder
 * request (`task`) or a draft role (`role`).
 *
 * @param {Object}    intl                React Intl, for the unlabelled remove button
 * @param {Function}  onEdit              Called with `{ task }` or `{ role }` to open it in the role builder
 * @param {Function}  onDiscardAIRequest  Called with a request to remove it
 * @param {Function}  onRemoveDraftRole   Called with a draft role to remove it
 * @param {Function}  onOpenDraftRole     Called with a draft that reached review, to open it
 * @param {Array}     removingRoleIds     Draft roles being removed, while their associations go
 */
const getTalentSimpleDraftColumns = (actions) => [
  {
    cell: ({ task, role }) =>
      task ? taskDisplayName(task) : <RoleLink mission={role.mission} missionRole={role} withOverlay />,
    width: "30%",
  },
  {
    cell: ({ task, role }) =>
      task ? (
        <InlineProgressBar
          label={<AIRoleBuilderTaskStage task={task} />}
          completed={task.stepsCompleted || 0}
          total={task.stepsTotal || 0}
        />
      ) : (
        <Row gap="0.25rem">
          <Tags data={role.disciplines} limit={3} unwrap {...TAG_COLORS.purple} size={TAG_SIZE.small} />
          <Tags data={role.primarySkills} limit={3} unwrap {...TAG_COLORS.pink} size={TAG_SIZE.small} />
        </Row>
      ),
    width: "30%",
  },
  {
    cell: ({ task, role }) => (task ? getTaskStatus(task) : getBuilderStepStatus(role)),
    width: "15%",
  },
  {
    cell: (row) => (
      <Row gap="0.5rem" noWrap css={styles.actions}>
        {getActions(row, actions)}
      </Row>
    ),
    width: "25%",
  },
];

const STATUS_TONES = {
  blue: { color: colors.lightBlue, background: colors.lightestBlue, dotColor: colors.blueVelvetBase },
  green: { color: colors.darkGreen, background: colors.lightGreen, dotColor: colors.green },
  red: { color: colors.darkRed, background: colors.lightRed, dotColor: colors.red },
};

const statusPill = (children, tone = STATUS_TONES.blue) => <Status {...tone}>{children}</Status>;

const getTaskStatus = (task) => {
  const displayState = getRoleBuilderTaskDisplayState(task);

  if (displayState === TASK_DISPLAY_STATE.READY) {
    return statusPill(
      <FormattedMessage id="Missions.Roles.TalentSimple.DraftsList.Status.ReadyForReview" />,
      STATUS_TONES.green
    );
  }

  if (displayState === TASK_DISPLAY_STATE.ERROR) {
    return statusPill(<FormattedMessage id="Missions.Roles.TalentSimple.DraftsList.Status.Failed" />, STATUS_TONES.red);
  }

  return statusPill(<FormattedMessage id="Missions.Roles.TalentSimple.DraftsList.Status.Analyzing" />);
};

const getBuilderStepStatus = (role) => {
  const index = getRoleBuilderStepIndex(role.builderStep);

  if (index === ROLE_BUILDER_STEPS.length - 1) {
    return statusPill(
      <FormattedMessage id="Missions.Roles.TalentSimple.DraftsList.Status.Completed" />,
      STATUS_TONES.green
    );
  }

  return statusPill(
    <FormattedMessage
      id="Missions.Roles.TalentSimple.DraftsList.Status.InProgressStep"
      values={{ current: index + 1, total: ROLE_BUILDER_STEPS.length }}
    />
  );
};

const getActions = (
  { task, role },
  { intl, onEdit, onDiscardAIRequest, onRemoveDraftRole, onOpenDraftRole, removingRoleIds }
) => {
  const editButton = (messageId, target) => (
    <SecondaryButton key="edit" size={BUTTON_SIZE.small} onClick={() => onEdit(target)}>
      <FormattedMessage id={messageId} />
    </SecondaryButton>
  );

  const removeButton = (onClick, removing) => (
    <DestructableButton
      key="remove"
      size={BUTTON_SIZE.small}
      onClick={onClick}
      disabled={removing}
      title={intl.formatMessage({ id: "Global.Delete" })}
    >
      {removing ? (
        <Spinner size={ICON_SIZE.medium} />
      ) : (
        <Icon type={ICON_TYPE.trash} size={ICON_SIZE.medium} color={colors.white} />
      )}
    </DestructableButton>
  );

  if (role) {
    const openButton = role.builderStep === ROLE_BUILDER_STEP.REVIEW && (
      <PrimaryButton key="open" size={BUTTON_SIZE.small} onClick={() => onOpenDraftRole(role)}>
        <FormattedMessage id="Missions.Roles.TalentSimple.RoleBuilder.Submit.CTA" />
      </PrimaryButton>
    );

    return [
      openButton,
      editButton("Missions.Roles.TalentSimple.DraftsList.Continue", { role }),
      removeButton(() => onRemoveDraftRole(role), removingRoleIds?.includes(role.id)),
    ].filter(Boolean);
  }

  const displayState = getRoleBuilderTaskDisplayState(task);

  if (displayState === TASK_DISPLAY_STATE.ERROR) return removeButton(() => onDiscardAIRequest(task));

  const editMessageId =
    displayState === TASK_DISPLAY_STATE.READY
      ? "AIRoleBuilder.Tasks.Review"
      : "Missions.Roles.TalentSimple.DraftsList.Continue";

  return [editButton(editMessageId, { task }), removeButton(() => onDiscardAIRequest(task))];
};

const styles = {
  actions: css`
    justify-content: flex-end;
  `,
};

export default getTalentSimpleDraftColumns;
